/**
 * Production-order display maps shared by every Tracker surface.
 *
 * These lived in `TrackerOrderTable.tsx` and were imported from there by
 * `TrackerQueueTable`. BLU-758 moved them out: the extracted
 * `ExpandedOrderContent` needs the same status vocabulary and is itself
 * imported BY `TrackerOrderTable`, so leaving them in a component file would
 * have created an import cycle. A data module has no such problem — and it also
 * clears the `react-refresh/only-export-components` warning those exports were
 * generating.
 *
 * Labels are i18n KEYS, never English strings: `TrackerOrderRow`'s local
 * `formatStatusLabel` is English-only and must not spread (the Thread Lab floor
 * runs the UI in zh).
 */
import { differenceInHours } from 'date-fns';

import type { RouterInputs } from '@/lib/trpc';

/**
 * The committed-tab production-status filter value. Derived from the tRPC input
 * enum so the backend allowlist is the single source of truth — includes every
 * production status plus the `NOT_IN_PRODUCTION` sentinel.
 */
export type CommittedProductionStatus = NonNullable<
  RouterInputs['productionOrders']['getCommittedOrdersSummary']['productionStatus']
>;

/** prod_orders.production_status → `production` namespace i18n key. */
export const STATUS_LABEL_KEYS: Record<string, string> = {
  NEW: 'tracker.orders.status.new',
  WAITING_STOCK: 'tracker.orders.status.waitingStock',
  IN_BATCH: 'tracker.orders.status.queued',
  IN_PROGRESS: 'tracker.orders.status.inProgress',
  // Mid-pipeline station stages (BLU-811): the Committed tab surfaces the
  // least-advanced live prod_order status at a floor, so these have to map or a
  // pretreat/catching/packing order would fall through to the humanised code.
  PRETREAT_QUEUE: 'tracker.orders.status.pretreatQueue',
  PRETREAT_READY: 'tracker.orders.status.pretreatReady',
  PRETREAT_COMPLETE: 'tracker.orders.status.pretreatComplete',
  CATCHING_READY: 'tracker.orders.status.catchingReady',
  CATCHING_COMPLETE: 'tracker.orders.status.catchingComplete',
  PACKING_READY: 'tracker.orders.status.packingReady',
  READY_TO_PACK: 'tracker.orders.status.readyToPack',
  PACKED: 'tracker.orders.status.packed',
  SHIPPED: 'tracker.orders.status.shipped',
  CANCELLED: 'tracker.orders.status.cancelled',
  ON_HOLD: 'tracker.orders.status.onHold',
  EXTERNALLY_FULFILLED: 'tracker.orders.status.extFulfilled',
  READY_FOR_PICKUP: 'tracker.orders.status.readyForPickup',
};

/**
 * The NULL production-status bucket (BLU-811): an order holding committed stock at a
 * floor with no LIVE production there. Kept as a first-class key so it can be a status
 * filter value and a breakdown row alongside the real statuses. Its label lives at
 * `tracker.committed.notInProduction`, NOT in STATUS_LABEL_KEYS, and it is deliberately
 * badge-less (never dressed in a status colour).
 */
export const NOT_IN_PRODUCTION = 'NOT_IN_PRODUCTION';

/** A status not in the ladder ranks last-but-one, mirroring the SQL `ELSE 999`. */
const UNKNOWN_STATUS_RANK = 999;

/**
 * Production-status ordering, LEAST-ADVANCED first. KEEP IN LOCKSTEP with the server
 * ladder in `public._committed_status_rank` — the shared IMMUTABLE helper in
 * `20260820100000_blu811_committed_status_filter.sql`. The ladder is the `CASE …` block
 * INSIDE that helper (itself matched to the original `CASE po.production_status …` in
 * `20260819130000_blu811_committed_tracker_tab.sql` that it was factored out of).
 * `list_committed_orders` and the summary breakdown both `ORDER BY` that helper, so a
 * drift here would list statuses in a different order than the server ranks them — the
 * Committed tab's status filter (option order) and the Production-status header
 * breakdown both read this. Every key in STATUS_LABEL_KEYS carries a rank here (so the
 * map is total); `NOT_IN_PRODUCTION` is always last, after every live stage. CANCELLED
 * is a STATUS_LABEL_KEYS member but is filtered OUT of the committed decoration
 * server-side (never a breakdown key) — it still gets a rank so the map stays total.
 */
export const STATUS_RANK: Record<string, number> = {
  NEW: 1,
  WAITING_STOCK: 2,
  ON_HOLD: 3,
  IN_BATCH: 4,
  PRETREAT_QUEUE: 5,
  PRETREAT_READY: 6,
  PRETREAT_COMPLETE: 7,
  IN_PROGRESS: 8,
  CATCHING_READY: 9,
  CATCHING_COMPLETE: 10,
  PACKING_READY: 11,
  READY_TO_PACK: 12,
  PACKED: 13,
  READY_FOR_PICKUP: 14,
  SHIPPED: 15,
  EXTERNALLY_FULFILLED: 16,
  // Terminal + excluded from the committed status decoration; ranked only to keep the
  // map total over STATUS_LABEL_KEYS.
  CANCELLED: 900,
  [NOT_IN_PRODUCTION]: 1000,
};

/** Rank for a status, tolerant of an unknown value (sorts last-but-one, per SQL ELSE). */
export function statusRank(status: string): number {
  return STATUS_RANK[status] ?? UNKNOWN_STATUS_RANK;
}

/**
 * Sort status keys least-advanced first by STATUS_RANK, tie-broken by the key itself
 * for determinism. Used by the Committed tab's status filter and the Production-status
 * header breakdown tooltip so both read in the same server-defined order.
 */
export function sortStatusKeysByRank(keys: string[]): string[] {
  return [...keys].sort((a, b) => statusRank(a) - statusRank(b) || a.localeCompare(b));
}

export const statusColors: Record<string, string> = {
  NEW: 'bg-slate-100 text-slate-700 dark:bg-slate-800/30 dark:text-slate-400',
  WAITING_STOCK: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  IN_BATCH: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  // Mid-pipeline stages reuse the existing ramp (no new hues; purple stays
  // reserved for fulfilment-type pills): QUEUE = blue like IN_BATCH, READY =
  // indigo like READY_TO_PACK, COMPLETE = teal like PACKED.
  PRETREAT_QUEUE: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  PRETREAT_READY: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  PRETREAT_COMPLETE: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  CATCHING_READY: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  CATCHING_COMPLETE: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  PACKING_READY: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  READY_TO_PACK: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  PACKED: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  SHIPPED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  CANCELLED: 'bg-slate-100 text-slate-600 dark:bg-slate-800/30 dark:text-slate-500',
  ON_HOLD: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  EXTERNALLY_FULFILLED: 'bg-slate-100 text-slate-600 dark:bg-slate-800/30 dark:text-slate-500',
  READY_FOR_PICKUP: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
};

/**
 * `orders.display_fulfillment_status` → `production` namespace i18n key.
 *
 * This is Shopify's `DisplayFulfillmentStatus`, stored verbatim by order-sync
 * (`order-sync.ts` writes `order.displayFulfillmentStatus ?? null`), so the map
 * covers the whole enum rather than only the values seen in the wild today — a
 * new Shopify value must degrade to the humanised code, never to a blank cell.
 */
export const FULFILLMENT_STATUS_LABEL_KEYS: Record<string, string> = {
  UNFULFILLED: 'tracker.fulfillment.unfulfilled',
  PARTIALLY_FULFILLED: 'tracker.fulfillment.partiallyFulfilled',
  FULFILLED: 'tracker.fulfillment.fulfilled',
  ON_HOLD: 'tracker.fulfillment.onHold',
  SCHEDULED: 'tracker.fulfillment.scheduled',
  IN_PROGRESS: 'tracker.fulfillment.inProgress',
  OPEN: 'tracker.fulfillment.open',
  PENDING_FULFILLMENT: 'tracker.fulfillment.pendingFulfillment',
  RESTOCKED: 'tracker.fulfillment.restocked',
  READY_FOR_PICKUP: 'tracker.fulfillment.readyForPickup',
};

/**
 * Fulfillment state is SHOPIFY's view of the order, not the floor's. It gets a
 * neutral outline so it cannot be mistaken for the production-status colour ramp
 * above (`statusColors`) — two coloured chips in one row would read as two
 * competing claims about the same order.
 */
export const FULFILLMENT_STATUS_BADGE_CLASS =
  'border-border bg-transparent text-muted-foreground font-normal';

/** POD / P&P / Mixed pill for an order's line-item composition. */
export function getFulfillmentType(podItems: number, papItems: number): { labelKey: string; className: string } {
  if (podItems > 0 && papItems > 0) {
    return { labelKey: 'tracker.orders.fulfillmentType.mixed', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' };
  }
  if (papItems > 0) {
    return { labelKey: 'tracker.orders.fulfillmentType.pap', className: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400' };
  }
  return { labelKey: 'tracker.orders.fulfillmentType.pod', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' };
}

/** Age colour ramp: green < 48h, amber < 6d, red beyond. */
export function getAgeColor(createdAt: string): string {
  const hours = differenceInHours(new Date(), new Date(createdAt));
  if (hours < 48) return 'text-green-600 dark:text-green-400';
  if (hours < 144) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}
